/**
 * 我的提现页面 - 运营商提现管理
 */

import { useState, useEffect } from 'react'
import { useAuthStore } from '@/store/authStore'
import { supabase } from '@/lib/supabase'
import { PlusIcon } from '@heroicons/react/24/outline'

interface Withdrawal {
  id: string
  amount: number
  fee: number
  real_amount: number
  status: string
  wallet_info?: {
    wallet_name: string
    channel_name: string
    channel_icon: string
  }
  created_at: string
  completed_at?: string
}

interface Wallet {
  id: string
  wallet_name: string
  wallet_address: string
  channel_code: string
  channel_name: string
  channel_icon: string
  is_default: boolean
}

export default function MyWithdrawalsPage() {
  const { user } = useAuthStore()
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([])
  const [wallets, setWallets] = useState<Wallet[]>([])
  const [loading, setLoading] = useState(true)
  const [showApplyModal, setShowApplyModal] = useState(false)
  
  // 表单状态
  const [formData, setFormData] = useState({
    amount: '',
    wallet_id: ''
  })

  // 运营商账户信息
  const [operatorAccount, setOperatorAccount] = useState({
    commission_balance: 0,
    total_commission: 0
  })

  useEffect(() => {
    fetchWithdrawals()
    fetchWallets()
    fetchOperatorAccount()
  }, [])

  const fetchWithdrawals = async () => {
    try {
      setLoading(true)
      
      // 获取 operator_id
      const { data: adminUser, error: adminError } = await supabase
        .from('admin_users')
        .select('operator_id')
        .eq('id', user?.id)
        .maybeSingle()

      if (adminError || !adminUser?.operator_id) {
        console.error('Failed to get operator_id:', adminError)
        setWithdrawals([])
        return
      }

      const { data, error } = await supabase
        .from('withdraw_orders')
        .select(`
          id, amount, fee, real_amount, status, created_at, completed_at,
          user_wallets(
            wallet_name,
            payment_channels(channel_name, channel_icon)
          )
        `)
        .eq('operator_id', adminUser.operator_id)
        .eq('order_type', 2)
        .order('created_at', { ascending: false })

      if (error) throw error

      const formattedData = (data || []).map((item: any) => ({
        id: item.id,
        amount: parseFloat(item.amount),
        fee: parseFloat(item.fee || '0'),
        real_amount: parseFloat(item.real_amount),
        status: item.status,
        wallet_info: item.user_wallets ? {
          wallet_name: item.user_wallets.wallet_name,
          channel_name: item.user_wallets.payment_channels?.channel_name || '',
          channel_icon: item.user_wallets.payment_channels?.channel_icon || ''
        } : undefined,
        created_at: item.created_at,
        completed_at: item.completed_at
      }))

      setWithdrawals(formattedData)
    } catch (error) {
      console.error('Failed to fetch withdrawals:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchWallets = async () => {
    try {
      const { data, error } = await supabase.rpc('get_user_wallets', { 
        p_owner_type: 'operator',
        p_owner_id: user?.id
      })

      if (error) throw error
      setWallets(data || [])
      
      // 自动选择默认钱包
      const defaultWallet = (data || []).find((w: Wallet) => w.is_default)
      if (defaultWallet) {
        setFormData(prev => ({ ...prev, wallet_id: defaultWallet.id }))
      }
    } catch (error) {
      console.error('Failed to fetch wallets:', error)
    }
  }

  const fetchOperatorAccount = async () => {
    try {
      // 第一步：从 admin_users 获取 operator_id
      const { data: adminUser, error: adminError } = await supabase
        .from('admin_users')
        .select('operator_id, role')
        .eq('id', user?.id)
        .maybeSingle()

      if (adminError) {
        console.error('Failed to fetch admin user:', adminError)
        return
      }

      if (!adminUser || adminUser.role !== 'operator' || !adminUser.operator_id) {
        console.warn('User is not an operator or has no operator_id')
        setOperatorAccount({
          commission_balance: 0,
          total_commission: 0
        })
        return
      }

      // 第二步：使用 operator_id 查询 operator_accounts
      const { data, error } = await supabase
        .from('operator_accounts')
        .select('commission_balance, total_commission')
        .eq('operator_id', adminUser.operator_id)
        .maybeSingle()

      if (error) {
        console.error('Failed to fetch operator account:', error)
        return
      }
      
      if (data) {
        setOperatorAccount({
          commission_balance: parseFloat(data.commission_balance || '0'),
          total_commission: parseFloat(data.total_commission || '0')
        })
      } else {
        // 如果没有记录，显示默认值 0
        console.warn('No operator account found for operator_id:', adminUser.operator_id)
        setOperatorAccount({
          commission_balance: 0,
          total_commission: 0
        })
      }
    } catch (error) {
      console.error('Failed to fetch operator account:', error)
      setOperatorAccount({
        commission_balance: 0,
        total_commission: 0
      })
    }
  }

  const handleApplyWithdrawal = async () => {
    try {
      const amount = parseFloat(formData.amount)
      
      if (!amount || amount <= 0) {
        alert('请输入有效的提现金额')
        return
      }

      if (amount > operatorAccount.commission_balance) {
        alert('提现金额超过可用余额')
        return
      }

      if (!formData.wallet_id) {
        alert('请选择提现钱包')
        return
      }

      // 获取 operator_id
      const { data: adminUser, error: adminError } = await supabase
        .from('admin_users')
        .select('operator_id')
        .eq('id', user?.id)
        .single()

      if (adminError || !adminUser?.operator_id) {
        throw new Error('无法获取运营商信息')
      }

      // 调用 RPC 函数创建运营商提现订单
      const { data, error } = await supabase.rpc('rpc_operator_withdraw', {
        p_operator_id: adminUser.operator_id,
        p_amount: amount,
        p_wallet_id: formData.wallet_id
      })

      if (error) throw error

      alert('提现申请已提交')
      setShowApplyModal(false)
      setFormData({ amount: '', wallet_id: '' })
      fetchWithdrawals()
      fetchOperatorAccount()
    } catch (error) {
      console.error('Failed to apply withdrawal:', error)
      alert('提现申请失败：' + (error as Error).message)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      pending: { label: '待审核', className: 'bg-yellow-100 text-yellow-800' },
      finance_approved: { label: '财务通过', className: 'bg-blue-100 text-blue-800' },
      audit_approved: { label: '审计通过', className: 'bg-purple-100 text-purple-800' },
      completed: { label: '已完成', className: 'bg-green-100 text-green-800' },
      rejected: { label: '已拒绝', className: 'bg-red-100 text-red-800' }
    }
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    )
  }

  return (
    <div className="page-content">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">我的提现</h1>
            <p className="page-subtitle">管理您的佣金提现</p>
          </div>
          <button
            onClick={() => setShowApplyModal(true)}
            className="btn btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>申请提现</span>
          </button>
        </div>
      </div>

      {/* 账户余额卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="card">
          <div className="card-body">
            <h3 className="text-sm font-medium text-gray-500 mb-2">可提现佣金</h3>
            <p className="text-3xl font-bold text-green-600">
              ¥{operatorAccount.commission_balance.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h3 className="text-sm font-medium text-gray-500 mb-2">累计佣金</h3>
            <p className="text-3xl font-bold text-blue-600">
              ¥{operatorAccount.total_commission.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>

      {/* 提现记录 */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">提现记录</h3>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-12">
              <div className="text-gray-500">加载中...</div>
            </div>
          ) : withdrawals.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500 mb-4">还没有提现记录</div>
              <button
                onClick={() => setShowApplyModal(true)}
                className="btn btn-primary"
              >
                申请第一笔提现
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      提现金额
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      手续费
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      实际到账
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      提现钱包
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      申请时间
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {withdrawals.map((withdrawal) => (
                    <tr key={withdrawal.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ¥{withdrawal.amount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-red-600">
                          ¥{withdrawal.fee.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-green-600">
                          ¥{withdrawal.real_amount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {withdrawal.wallet_info ? (
                          <div className="flex items-center space-x-2">
                            <span>{withdrawal.wallet_info.channel_icon}</span>
                            <div>
                              <div className="text-sm text-gray-900">{withdrawal.wallet_info.wallet_name}</div>
                              <div className="text-xs text-gray-500">{withdrawal.wallet_info.channel_name}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(withdrawal.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(withdrawal.created_at).toLocaleString('zh-CN')}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 申请提现模态框 */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">申请提现</h3>

              <div className="space-y-4">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-sm text-blue-800">
                    可提现余额: <span className="font-bold">¥{operatorAccount.commission_balance.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">提现金额</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="请输入提现金额"
                    min="0"
                    step="0.01"
                  />
                  <p className="mt-1 text-xs text-gray-500">手续费: 2.5%</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">选择钱包</label>
                  {wallets.length === 0 ? (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                      还没有添加钱包，请先在"我的钱包"页面添加
                    </div>
                  ) : (
                    <select
                      value={formData.wallet_id}
                      onChange={(e) => setFormData({ ...formData, wallet_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">请选择钱包</option>
                      {wallets.map((wallet) => (
                        <option key={wallet.id} value={wallet.id}>
                          {wallet.channel_icon} {wallet.wallet_name} - {wallet.channel_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {formData.amount && parseFloat(formData.amount) > 0 && (
                  <div className="p-3 bg-gray-50 rounded-lg space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">提现金额:</span>
                      <span className="font-semibold">¥{parseFloat(formData.amount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">手续费 (2.5%):</span>
                      <span className="text-red-600">-¥{(parseFloat(formData.amount) * 0.025).toFixed(2)}</span>
                    </div>
                    <div className="h-px bg-gray-200"></div>
                    <div className="flex justify-between">
                      <span className="text-gray-900 font-medium">实际到账:</span>
                      <span className="text-green-600 font-bold">¥{(parseFloat(formData.amount) * 0.975).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowApplyModal(false)
                    setFormData({ amount: '', wallet_id: '' })
                  }}
                  className="btn btn-secondary"
                >
                  取消
                </button>
                <button
                  onClick={handleApplyWithdrawal}
                  className="btn btn-primary"
                  disabled={!formData.amount || !formData.wallet_id || wallets.length === 0}
                >
                  确认提现
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
